import {
  Box,
  Button,
  Divider,
  IconButton,
  Input,
  Stack,
  styled,
  Text,
} from '@ltvco/refresh-lib/theme';
import { ArrowUpward, Menu, Search } from '@mui/icons-material';

// Interfaces
interface SidebarProps {
  isHovered?: boolean;
  isAnimatingOut?: boolean;
}
interface LandingPageContainerProps {
  landingOpacity: number;
  isTransitioning: boolean;
}

interface ChatPageInputContainerProps {
  isLanding?: boolean;
}

interface AnimatedInputContainerProps {
  isLanding?: boolean;
  isAnimating?: boolean;
  animationPhase?: 'center' | 'moving-up' | 'moving-left' | 'header';
  termsPosition?: { top: number; left: number; width: number };
  showSuggestions?: boolean;
}

interface AnimatedButtonContainerProps {
  isReportOpen?: boolean;
  isLanding?: boolean;
  isAnimating?: boolean;
}

interface AnimatedSendButtonProps {
  isAnimating?: boolean;
}

interface ButtonProps {
  hasInputValue: boolean;
  isLanding?: boolean;
}

//Landing Section Components

export const ChatBvLogoButton = styled(Button)({
  padding: '0 16px 0 16px',
  '&.MuiButtonBase-root:hover': {
    backgroundColor: 'unset !important',
  },
});

export const LandingPageContainer = styled(Box)<LandingPageContainerProps>(
  ({ theme, landingOpacity, isTransitioning }) => ({
    padding: '30px 15px',
    height: '95%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    width: '100%',
    backgroundColor: theme.palette.background.paper,
    opacity: landingOpacity,
    transition: 'opacity 0.5s cubic-bezier(0.4, 0.0, 0.2, 1)',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: isTransitioning ? 1 : 2,
    pointerEvents: landingOpacity === 0 ? 'none' : 'auto',
    '@media (min-width: 1024px)': {
      height: '100%',
    },
    [theme.breakpoints.up('md')]: {
      padding: '30px',
    },
  })
);

export const StyledInput = styled(Input)(() => ({
  border: 'unset',
  width: '100%',
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'unset',
  },
}));

export const SendButton = styled(Button)<ButtonProps>(
  ({ theme, hasInputValue }) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: hasInputValue ? theme.palette.primary.main : 'transparent',
    color: hasInputValue
      ? theme.palette.text.contrastText
      : theme.palette.text.secondary,
    border: 'unset',
    borderRadius: '50%',
    width: '40px',
    height: '40px',
    padding: '0',
    minWidth: 'unset',
    transition: 'background-color 0.2s ease',
    marginRight: '10px !important',
    marginBottom: '7px',
    '&:hover': {
      backgroundColor: hasInputValue
        ? theme.palette.primary.dark
        : 'transparent',
    },
  })
);

export const ArrowUpwardIcon = styled(ArrowUpward)<ButtonProps>(
  ({ theme, hasInputValue }) => ({
    width: '20px',
    height: '20px',
    color: hasInputValue
      ? theme.palette.text.contrastText
      : theme.palette.text.secondary,
    transition: 'color 0.2s ease',
  })
);

export const NewChatButton = styled(Button)(() => ({
  width: '32px',
  height: '32px',
  minWidth: '32px',
  borderRadius: '50%',
}));

export const ChatChipsContainer = styled(Box)(({ theme }) => ({
  height: '38px',
  width: 'auto',
  minWidth: '120px',
  borderRadius: '20px',
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  display: 'flex',
  alignItems: 'center',
  padding: '0 12px',
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    borderColor: theme.palette.primary.main,
  },
}));

export const ChatChipsCircleIcon = styled(Box)(({ theme }) => ({
  width: '24px',
  height: '24px',
  borderRadius: '50%',
  backgroundColor: theme.palette.primary.light,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

export const LandingTextSectionStack = styled(Stack)(({ theme }) => ({
  flexDirection: 'column',
  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    fontSize: '36px',
  },
}));

// Chat Page Components
export const ChatPageContainer = styled(Box)(() => ({
  height: '100vh',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'space-around',
  position: 'relative',
  '@media (min-width: 1025px)': {
    flexDirection: 'row',
    justifyContent: 'flex-start',
  },
}));

export const ChatPageBoxContainer = styled(Box)<SidebarProps>(
  ({ theme, isAnimatingOut }) => ({
    backgroundColor: theme.palette.background.default,
    animation: isAnimatingOut
      ? 'slideOut 0.3s ease-in'
      : 'slideIn 0.3s ease-out',
    '@keyframes slideIn': {
      '0%': { transform: 'translateX(-100%)' },
      '100%': { transform: 'translateX(0)' },
    },
    '@keyframes slideOut': {
      '0%': { transform: 'translateX(0)' },
      '100%': { transform: 'translateX(-100%)' },
    },
  })
);

export const ChatPageContainerStack = styled(Stack)(({ theme }) => ({
  alignItems: 'center',
  overflowY: 'auto',
  backgroundColor: theme.palette.background.paper,
  borderRadius: '16px',
  padding: '0 10px 10px',
  height: '98%',
  margin: '30px 0',
  [theme.breakpoints.up('md')]: {
    padding: '42px 10px 10px',
  },
  '@media (min-width: 1024px)': {
    margin: '8px',
  },
}));

export const MessagesContentStack = styled(Stack)(() => ({
  overflowY: 'auto',
  minHeight: '0',
  flex: 1,
  width: '99%',
}));

export const ChatPageMessagesContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100%',
  flexGrow: 1,
  [theme.breakpoints.up('md')]: {
    flexGrow: 1,
  },
}));

export const MessageBoxContainer = styled(Box)(({ theme }) => ({
  color: theme.palette.text.primary,
  width: '100%',
  marginBottom: '10px',
  maxWidth: '100%',
  margin: '0px',
}));

// Chat Page Sidebar Components
export const ChatPageSidebarContainer = styled(Box)({
  height: '100%',
  zIndex: 1,
  top: 0,
});

export const SidebarIcon = styled(Box)(({ theme }) => ({
  width: '20px',
  height: '20px',
  color: theme.palette.text.secondary,
  cursor: 'pointer',
  '&:hover': {
    color: theme.palette.primary.main,
  },
}));

export const SidebarText = styled(Text)<SidebarProps>(({ isHovered }) => ({
  opacity: isHovered ? 1 : 0,
  transform: isHovered ? 'translateX(0)' : 'translateX(-10px)',
  transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
  transitionDelay: '0.1s',
  whiteSpace: 'nowrap',
  pointerEvents: isHovered ? 'auto' : 'none',
  visibility: isHovered ? 'visible' : 'hidden',
  width: isHovered ? 'auto' : '0px',
  overflow: 'hidden',
}));

export const SidebarStack = styled(Stack)<SidebarProps>(({ isHovered }) => ({
  width: '100%',
  paddingLeft: isHovered ? '20px' : '0px',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
}));

// Chat Page Input Components

export const ChatPageInputContainer = styled(Box)<ChatPageInputContainerProps>(
  ({ theme, isLanding }) => ({
    backgroundColor: theme.palette.background.paper,
    padding: '10px 10px 0',
    borderRadius: '10px',
    width: '100%',
    marginBottom: '0',
    position: 'sticky',
    bottom: 0,
    zIndex: 10,
    '@media (min-width: 1024px)': {
      padding: '10px 0',
      marginBottom: 'clamp(40px, 8vh, 120px)', // Responsive margin based on viewport height
      position: 'relative',
      zIndex: 0,
      maxWidth: '740px',
      width: '98%',
    },
    ...(isLanding && {
      width: '100%',
      padding: '10px 0 0',
      maxWidth: '100%',
      margin: '0 auto 32px auto',
      position: 'relative',
      transition: 'all 1.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
      [theme.breakpoints.up('md')]: {
        width: '60%',
      },
    }),
  })
);

export const AnimatedInputContainer = styled(Box)<AnimatedInputContainerProps>(
  ({ theme, isLanding, isAnimating, animationPhase, termsPosition }) => ({
    backgroundColor: theme.palette.background.paper,
    padding: '10px 10px 0',
    borderRadius: '10px',
    width: '100%',
    marginBottom: '0',
    position: 'sticky',
    bottom: 0,
    zIndex: 10,
    ...(isLanding && {
      position: 'relative',
    }),
    '@media (min-width: 1024px)': {
      padding: '10px 0 0',
      marginBottom: 'clamp(40px, 6vh, 120px)',
      position: 'relative',
      zIndex: 0,
      maxWidth: '740px',
      width: '98%',
    },
    [theme.breakpoints.up('md')]: {
      padding: '4px 10px 10px',
      margin: '0 !important',
    },
    ...(isLanding && {
      width: '100%',
      padding: '10px 0 0',
      maxWidth: '100%',
      margin: '0 auto 32px auto',
      position: 'relative',
      transition: 'all 1.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
      [theme.breakpoints.up('md')]: {
        width: '60%',
      },

      ...(isAnimating && {
        zIndex: 20,
        transition: 'transform 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
        // Moving the input down according to logo animation
        ...(animationPhase === 'moving-up' && {
          transform: `translateY(${termsPosition?.top || 0}px)`,
          position: 'relative',
        }),
        ...(animationPhase === 'moving-left' && {
          transform: `translateY(${termsPosition?.top || 0}px)`,
          position: 'relative',
        }),
        // Completing the input animation according to logo animation
        ...(animationPhase === 'header' && {
          transform: `translateY(${termsPosition?.top || 0}px)`,
          position: 'relative',
        }),
      }),
    }),
  })
);

export const AnimatedInputButtonContainer = styled(
  Stack
)<AnimatedInputContainerProps>(
  ({ theme, isLanding, isAnimating, animationPhase, showSuggestions }) => ({
    flexDirection: isLanding ? 'column' : 'row',
    border: 'none',
    borderRadius: showSuggestions ? '16px 16px 0 0' : '16px',
    position: 'relative',
    background:
      'linear-gradient(271.66deg, rgba(20, 138, 230, 0.86) 1.32%, rgba(81, 201, 245, 0.86) 5.5%, rgba(0, 82, 255, 0.86) 53.66%, rgba(146, 216, 73, 0.7998) 113.22%)',
    transition: 'border-radius 0.15s ease-out',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: '3px',
      left: '3px',
      right: '3px',
      bottom: showSuggestions ? '0px' : '3px',
      background: `${theme.palette.background.paper}`,
      borderRadius: showSuggestions ? '13px 13px 0 0' : '13px',
      zIndex: 1,
      transition: 'bottom 0.15s ease-out, border-radius 0.15s ease-out',
    },
    '& > *': {
      position: 'relative',
      zIndex: 2,
    },
    alignItems: 'flex-end',
    height: isLanding ? 'auto' : 'auto',
    minHeight: isLanding ? 'auto' : '56px',
    padding: isLanding ? '13px' : '3px',
    width: isLanding ? '100%' : 'auto',
    marginBottom: 0,
    boxShadow: isLanding
      ? `0px 2px 15px 0px ${theme.palette.divider}`
      : `0px 4px 8px -2px ${theme.palette.divider}`,

    // Styles for landing animation
    ...(isLanding && {
      transition: 'all 2s cubic-bezier(0.4, 0, 0.2, 1)',
      boxShadow: '0px 2px 15px 0px rgba(0,0,0,0.1)',
      overflow: 'hidden',

      ...(isAnimating &&
        animationPhase === 'moving-up' && {
          maxHeight: '70px',
          height: '70px',
          padding: '8px 16px',
          flexDirection: 'row',
          alignItems: 'center',
          boxShadow: '0px 3px 10px 0px rgba(0,0,0,0.1)',
          borderRadius: '12px',
        }),
      ...(isAnimating &&
        animationPhase === 'moving-left' && {
          maxHeight: '60px',
          height: '60px',
          padding: '4px 16px',
          flexDirection: 'row',
          alignItems: 'center',
          boxShadow: '0px 3px 8px -1px rgba(0,0,0,0.1)',
          borderRadius: '14px',
        }),
      ...(isAnimating &&
        animationPhase === 'header' && {
          height: '56px',
          padding: '0',
          flexDirection: 'row',
          alignItems: 'center',
          borderRadius: '16px',
          width: '97%',
          border: '1px solid var(--mui-palette-divider)',
          boxShadow: '0px 4px 8px -2px rgba(0,0,0,0.1)',
          position: 'sticky',
          bottom: 0,
          zIndex: 10,
          '@media (min-width: 1024px)': {
            position: 'relative',
            zIndex: 0,
            maxWidth: '740px',
          },
        }),
    }),
  })
);

export const StyledInputChat = styled('textarea')(() => ({
  border: 'unset',
  width: '97%',
  borderRadius: '16px',
  resize: 'none',
  outline: 'none',
  fontFamily: 'inherit',
  fontSize: '16px',
  lineHeight: '1.5',
  padding: '16px',
  backgroundColor: 'transparent',
  color: 'inherit',
  minHeight: '24px',
  maxHeight: '120px',
  overflowX: 'hidden',
  overflowY: 'auto',
  '&::placeholder': {
    color: 'inherit',
    opacity: 0.6,
  },
}));

export const ExpandedPlaceholderContainer = styled(Box)(() => ({
  position: 'relative',
  width: '100%',
  display: 'flex',
  alignItems: 'flex-start',
  minHeight: '56px',
  padding: '0 14px',
  borderRadius: '16px',
  backgroundColor: 'transparent',
}));

export const ExpandedPlaceholderText = styled(Text)<{ textWidth: number }>(
  ({ theme, textWidth }) => ({
    position: 'absolute',
    left: `${14 + textWidth + 8}px`,
    top: '19px',
    color: theme.palette.text.disabled,
    fontSize: '16px',
    opacity: 0.6,
    fontStyle: 'italic',
    pointerEvents: 'none',
    zIndex: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: `calc(100% - ${14 + textWidth + 8 + 14}px)`,
    right: '14px',
  })
);

export const StyledInputChatWithExpandedPlaceholder = styled('textarea')(
  () => ({
    border: 'unset',
    width: '100%',
    borderRadius: '16px',
    backgroundColor: 'transparent',
    resize: 'none',
    outline: 'none',
    fontFamily: 'inherit',
    fontSize: '16px',
    lineHeight: '1.5',
    padding: '16px',
    color: 'inherit',
    minHeight: '24px',
    maxHeight: '120px',
    overflowX: 'hidden',
    overflowY: 'auto',
    position: 'relative',
    zIndex: 3,
    '&::placeholder': {
      color: 'inherit',
      opacity: 0.6,
    },
  })
);
export const AnimatedButtonContainer = styled(
  Stack
)<AnimatedButtonContainerProps>(({ isReportOpen, isLanding, isAnimating }) => ({
  flexDirection: 'row',
  gap: '8px',
  width: isReportOpen ? '0' : '12%',
  marginTop: isLanding && !isAnimating ? '36px' : '0',
  justifyContent: 'end',
  alignItems: 'center',
  display: 'flex',
  padding: isLanding && !isAnimating ? '10px' : '0',
  borderRadius: isLanding && !isAnimating ? '16px' : '0',
  opacity: 1,
  transition:
    'opacity 0.3s ease-in-out, padding 0.3s ease-in-out, margin-top 0.3s ease-in-out',
  minWidth: '40px',
  height: '100%',
  ...(isAnimating && {
    alignItems: 'center',
    justifyContent: 'end',
    padding: '0 4px',
    minWidth: '48px',
    height: '100%',
    flexShrink: 0,
    marginTop: '0',
  }),
}));

export const AnimatedSendButton = styled('div')<AnimatedSendButtonProps>(
  ({ isAnimating }) => ({
    opacity: 1,
    visibility: 'visible',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ...(isAnimating && {
      position: 'relative',
      margin: '0',
      width: '32px',
      height: '32px',
      flexShrink: 0,
    }),
  })
);

// Chat Page Report Column Components

export const ReportColumnContainer = styled(Stack)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: '16px',
  margin: '0',
  overflowY: 'auto',
  paddingTop: 0,
  '@media (min-width: 1024px)': {
    margin: '8px',
    marginLeft: '0',
  },
}));

export const PersonIconContainer = styled(Stack)(({ theme }) => ({
  borderRadius: '50%',
  backgroundColor: theme.palette.background.default,
  padding: '10px 0',
  width: '80px',
  height: '80px',
  alignItems: 'center',
  [theme.breakpoints.up('md')]: {
    padding: '10px',
  },
}));

export const ResizeButton = styled(Button)(() => ({
  top: '10px',
  left: '10px',
  zIndex: 1000,
  minWidth: 0,
  padding: 0,
  margin: 0,
  width: '30px',
  position: 'unset',
  height: '30px',
}));

// Chat Page Divider Components

export const AdjustedDivider = styled(Divider)(({ theme }) => ({
  width: '100%',
  margin: '20px 0 !important',
  border: `1px solid ${theme.palette.divider}`,
}));

export const MessageContainerDivider = styled(Divider)(({ theme }) => ({
  width: '100%',
  marginBottom: '10px',
  marginTop: '10px',
  border: `1px solid ${theme.palette.divider}`,
}));

// Chat Page Menu Components
export const ChatMenuStack = styled(Stack)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  padding: '10px 10px 10px 0',
  width: '100%',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  position: 'sticky',
  top: '0',
  zIndex: '100',
  flexShrink: 0,
}));

export const MenuIcon = styled(Menu)(({ theme }) => ({
  color: theme.palette.text.secondary,
  cursor: 'pointer',
  '&:hover': {
    color: theme.palette.primary.main,
  },
}));

// Autosuggestion Components
export const AutosuggestionContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  backgroundColor: 'transparent',
  borderRadius: '0 0 16px 16px',
  background:
    'linear-gradient(271.66deg, rgba(20, 138, 230, 0.86) 1.32%, rgba(81, 201, 245, 0.86) 5.5%, rgba(0, 82, 255, 0.86) 53.66%, rgba(146, 216, 73, 0.7998) 113.22%)',
  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
  zIndex: 1000,
  marginTop: '-3px',
  padding: '3px',
  opacity: 0,
  visibility: 'hidden',
  transform: 'translateY(-10px)',
  transition:
    'opacity 0.15s ease-out, visibility 0.15s ease-out, transform 0.15s ease-out',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '0px',
    left: '3px',
    right: '3px',
    bottom: '3px',
    background: `${theme.palette.background.paper}`,
    borderRadius: '0 0 13px 13px',
    zIndex: -1,
  },
  '&.visible': {
    opacity: 1,
    visibility: 'visible',
    transform: 'translateY(0)',
  },
}));

export const AutosuggestionTitle = styled(Text)(({ theme }) => ({
  fontSize: '14px',
  fontWeight: 600,
  color: theme.palette.text.primary,
  marginBottom: '12px',
  display: 'block',
  padding: '16px 16px 0 16px',
}));

export const AutosuggestionList = styled(Stack)(() => ({
  flexDirection: 'column',
  gap: '8px',
  padding: '0 16px 16px 16px',
}));

export const AutosuggestionItem = styled(Button)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '12px 16px',
  borderRadius: '12px',
  backgroundColor: 'transparent',
  color: theme.palette.text.primary,
  textTransform: 'none',
  justifyContent: 'flex-start',
  fontSize: '14px',
  fontWeight: 400,
  minHeight: '48px',
  border: '1px solid transparent',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    borderColor: theme.palette.primary.light,
  },
}));

export const AutosuggestionIcon = styled(Search)(({ theme }) => ({
  width: '16px',
  height: '16px',
  color: theme.palette.text.secondary,
  flexShrink: 0,
}));

//Click here tooltip
export const ClickHereTooltipContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: '100%',
  margin: '0 auto 8px',
  left: '90%',
  transform: 'translateX(-90%)',
  width: '300px',
  backgroundColor: 'rgba(61, 61, 61, 0.9)',
  color: 'white',
  borderRadius: '8px',
  padding: '16px',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  zIndex: 1000,

  [theme.breakpoints.up('sm')]: {
    width: '400px',
    left: '50%',
    transform: 'translateX(-50%)',
    margin: '0 0 8px',
  },

  [theme.breakpoints.up('md')]: {
    width: '678px',
  },
}));

export const ClickHereTooltipArrow = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '100%',
  left: '90%',
  transform: 'translateX(-90%)',
  width: 0,
  height: 0,
  borderLeft: '6px solid transparent',
  borderRight: '6px solid transparent',
  borderTop: '6px solid rgba(61, 61, 61, 0.9)',
  [theme.breakpoints.up('sm')]: {
    left: '50%',
    transform: 'translateX(-50%)',
  },
}));

export const CloseIconButtonDisclaimer = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: '2px',
  right: '-25px',
  width: '20px',
  height: '20px',
  color: theme.palette.rats.main,
  zIndex: 1001,
}));
